import React, { useState, useEffect, useRef } from 'react';
import './Home.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  Bell, Search, TrendingUp, Wallet, 
  ArrowDownCircle, ArrowUpCircle, HelpCircle 
} from 'lucide-react';

// Import the new file (Make sure it's in the same folder)
import LiveWithdrawal from './LiveWithdrawal'; 

const Home = ({ onCoinClick }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUserData(JSON.parse(savedUser));
    }
  }, []);

  const initialCoins = [
    { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png', binanceSymbol: 'btcusdt' },
    { id: 'ethereum', symbol: 'ETH', name: 'Ethereum', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png', binanceSymbol: 'ethusdt' },
    { id: 'binancecoin', symbol: 'BNB', name: 'BNB', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/825/large/binance-coin-logo.png', binanceSymbol: 'bnbusdt' },
    { id: 'solana', symbol: 'SOL', name: 'Solana', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/4128/large/solana.png', binanceSymbol: 'solusdt' },
    { id: 'ripple', symbol: 'XRP', name: 'XRP', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png', binanceSymbol: 'xrpusdt' },
    { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/5/large/dogecoin.png', binanceSymbol: 'dogeusdt' },
    { id: 'tron', symbol: 'TRX', name: 'TRON', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/1094/large/tron-logo.png', binanceSymbol: 'trxusdt' },
    { id: 'tether', symbol: 'USDT', name: 'Tether', price: '1.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/325/large/tether.png', binanceSymbol: 'usdttry' },
    { id: 'cardano', symbol: 'ADA', name: 'Cardano', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/975/large/cardano.png', binanceSymbol: 'adausdt' },
    { id: 'polkadot', symbol: 'DOT', name: 'Polkadot', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/12171/large/polkadot.png', binanceSymbol: 'dotusdt' },
    { id: 'matic-network', symbol: 'MATIC', name: 'Polygon', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/4713/large/matic-token-icon.png', binanceSymbol: 'maticusdt' },
    { id: 'litecoin', symbol: 'LTC', name: 'Litecoin', price: '0.00', change: '0%', icon: 'https://assets.coingecko.com/coins/images/2/large/litecoin.png', binanceSymbol: 'ltcusdt' },
  ];

  const [coins, setCoins] = useState(initialCoins);
  const ws = useRef(null);

  const bannerImages = [
    "/banners/35f4f02d-a8be-4231-a5f3-48439e8996d7.jpg",
    "/banners/9747dd63-c423-41d0-8a13-f2b17e329653.jpg",
    "/banners/b9e7c6b4-f226-4ec7-aac5-bcc77905e876.jpg",
    "/banners/b8221360-649b-477a-9474-e7278becff94.jpg"
  ];

  useEffect(() => {
    const streams = initialCoins.map(c => `${c.binanceSymbol}@ticker`).join('/');
    ws.current = new WebSocket(`wss://stream.binance.com:9443/ws/${streams}`);

    ws.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      const tickerSymbol = message.s.toLowerCase();

      setCoins(prevCoins => prevCoins.map(coin => {
        if (coin.binanceSymbol === tickerSymbol) {
          return {
            ...coin,
            price: parseFloat(message.c).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
            change: (parseFloat(message.P) >= 0 ? '+' : '') + parseFloat(message.P).toFixed(2) + '%'
          };
        }
        return coin;
      }));
    };

    return () => { if (ws.current) ws.current.close(); };
  }, []);

  // Help Modal Logic
  const handleHelp = () => {
    Swal.fire({
      title: '<strong>Help & Support</strong>',
      icon: 'question',
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.6;">
          <h4 style="color: #f3ba2f; margin-bottom: 5px;">1. How to Deposit?</h4>
          <p>Go to the <b>Deposit</b> section, enter your USDT amount (Min 10 USDT), and transfer to the provided TRC20 address. Funds arrive in 1-3 minutes.</p>
          
          <h4 style="color: #f3ba2f; margin-top: 15px; margin-bottom: 5px;">2. Trading Basics</h4>
          <p>Select any coin from Market Trends to start trading. You can place Short or Long positions based on real-time market movements.</p>
          
          <h4 style="color: #f3ba2f; margin-top: 15px; margin-bottom: 5px;">3. Withdrawal Process</h4>
          <p>Ensure your <b>Payment Method</b> is set up. Withdrawals are processed through the TRC20 network and usually take 5-30 minutes for audit.</p>
          
          <h4 style="color: #f3ba2f; margin-top: 15px; margin-bottom: 5px;">4. Security Policy</h4>
          <p>We use high-end encryption to secure your assets. Never share your password or private details with anyone.</p>
        </div>
      `,
      showCloseButton: true,
      confirmButtonText: 'Got it!',
      confirmButtonColor: '#f3ba2f'
    });
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <div className="user-profile">
          <div className="avatar">
            {userData ? userData.username.substring(0, 2).toUpperCase() : 'TR'}
          </div>
          <span className="user-name">
            Hi, {userData ? userData.username : 'Trader'}
          </span>
        </div>
        <div className="header-icons"><Search size={22} /><Bell size={22} /></div>
      </div>

      <div className="slider-wrapper">
        <Swiper 
          modules={[Autoplay, Pagination]} 
          autoplay={{ delay: 2500, disableOnInteraction: false }} 
          pagination={{ clickable: true }} 
          className="banner-slider"
        >
          {bannerImages.map((imgUrl, index) => (
            <SwiperSlide key={index}>
              <div className="banner-card-new" style={{ height: '180px', borderRadius: '15px', overflow: 'hidden' }}>
                <img 
                  src={imgUrl} 
                  alt={`Slide ${index + 1}`} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <LiveWithdrawal />

      <div className="quick-actions-grid">
        {/* Wallet -> Navigates to Payment Method */}
        <div className="action-item" onClick={() => navigate('/payment-method', { state: { activeTab: 'home' } })}>
          <div className="action-icon-circle blue-theme"><Wallet size={24} /></div>
          <span>Wallet</span>
        </div>
        
        {/* Deposit -> Navigates to Deposit Page */}
        <div className="action-item" onClick={() => navigate('/deposit', { state: { activeTab: 'home' } })}>
          <div className="action-icon-circle blue-theme"><ArrowDownCircle size={24} /></div>
          <span>Deposit</span>
        </div>

        {/* Withdraw -> Navigates to Withdraw Page */}
        <div className="action-item" onClick={() => navigate('/withdraw', { state: { activeTab: 'home' } })}>
          <div className="action-icon-circle blue-theme"><ArrowUpCircle size={24} /></div>
          <span>Withdraw</span>
        </div>

        {/* Help -> Triggers Help Modal */}
        <div className="action-item" onClick={handleHelp}>
          <div className="action-icon-circle blue-theme"><HelpCircle size={24} /></div>
          <span>Help</span>
        </div>
      </div>

      <div className="market-title">
        <TrendingUp size={18} color="#f3ba2f" />
        <span>Market Trends</span>
      </div>

      <div className="coin-list-header">
        <span>Asset / Name</span>
        <span>Price / Change</span>
      </div>

      <div className="coin-list">
        {coins.map((coin) => (
          <div className="coin-row" key={coin.id} onClick={() => onCoinClick(coin)}>
            <div className="coin-info">
              <div className="coin-icon-wrapper-small"><img src={coin.icon} alt={coin.symbol} className="coin-img-small" /></div>
              <div className="coin-name-symbol">
                <div className="symbol-small">{coin.symbol}<span className="pair-small">/USDT</span></div>
                <div className="name-small">{coin.name}</div>
              </div>
            </div>
            <div className="coin-price-section">
              <div className="price-small">${coin.price}</div>
              <div className={`change-tag-compact ${coin.change.startsWith('+') ? 'up' : 'down'}`}>{coin.change}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Home;